import { CanceledError } from 'axios';

const listKeys = new Set(['data', 'polygons', 'children', 'sheets', 'results', 'items', 'features', 'monuments', 'areas']);
const keyFor = item => {
    if (!item || typeof item !== 'object') return JSON.stringify(item);
    const sheet = typeof item.sheet === 'string' ? item.sheet : item.sheet?.name ?? item.properties?.sheet ?? '';
    const category = item.category ?? item.type_code ?? item.type?.code ?? '';
    const id = item.id ?? item.properties?.id ?? item.og_lrt_id ?? item.polygon_id ?? item.code;
    return id == null ? (category ? JSON.stringify([sheet, 'category', category]) : JSON.stringify(item))
        : JSON.stringify([sheet, category, String(id)]);
};

export function mergePublicMapPages(previous, next, key = '') {
    if (Array.isArray(previous) && Array.isArray(next)) {
        if (key && !listKeys.has(key)) return previous; // Coordinates/legend values are not record lists.
        const result = previous.slice();
        const positions = new Map(result.map((item, index) => [keyFor(item), index]));
        for (const item of next) {
            const itemKey = keyFor(item);
            if (positions.has(itemKey)) {
                const index = positions.get(itemKey);
                result[index] = mergePublicMapPages(result[index], item);
            } else {
                positions.set(itemKey, result.length);
                result.push(item);
            }
        }
        return result;
    }
    if (previous && next && typeof previous === 'object' && typeof next === 'object'
        && !Array.isArray(previous) && !Array.isArray(next)) {
        const result = { ...previous };
        for (const [name, value] of Object.entries(next)) {
            if (name === 'public_pagination') result[name] = value;
            else if (!(name in result)) result[name] = value;
            else result[name] = mergePublicMapPages(result[name], value, name);
        }
        return result;
    }
    return previous; // Counts and scalar metadata describe the complete snapshot.
}

export function isPublicMapRequest(config, origin) {
    let url;
    try { url = new URL(config.url, new URL(config.baseURL || origin, origin)); } catch { return false; }
    if (url.origin !== origin) return false;
    if (url.pathname === '/omnia/spatial/map') return true;
    return /^\/api\/pompei\/(?:topographic-referents\/(?:roots|at-point|children:batch|[^/]+(?:\/children)?)|cartografie\/search|scavi-storici\/(?:search|uc-polygons))$/.test(url.pathname);
}

function nextRequestConfig(config, pagination) {
    const values = { public_page: pagination.next_page, public_snapshot: pagination.snapshot, public_revision: pagination.revision };
    const result = { ...config, __publicMapFollowup: true };
    if (String(config.method || 'get').toLowerCase() === 'get') {
        result.params = config.params instanceof URLSearchParams ? new URLSearchParams(config.params) : { ...config.params };
        for (const [name, value] of Object.entries(values)) {
            if (result.params instanceof URLSearchParams) result.params.set(name, value);
            else result.params[name] = value;
        }
    } else {
        const data = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
        result.data = { ...data, ...values };
    }
    return result;
}

// Resolve only after every page has the same authorization/data revision. The
// existing map consumers receive one complete envelope and never a mixed tree.
export function installPublicMapPagination({ http, isPublic, identity, origin }) {
    http?.interceptors.request?.use(config => {
        if (isPublicMapRequest(config, origin) && !config.__publicMapFollowup) config.__publicMapIdentity = identity();
        return config;
    });
    return http?.interceptors.response?.use(async response => {
        const initialConfig = response.config;
        if (initialConfig?.__publicMapIdentity != null && initialConfig.__publicMapIdentity !== identity()) {
            throw new CanceledError('Public map audience changed');
        }
        if (!isPublic() || initialConfig?.__publicMapFollowup || !isPublicMapRequest(initialConfig || {}, origin)) return response;
        let pagination = response.data?.meta?.public_pagination;
        if (!pagination) throw new Error('Missing public map pagination');
        const snapshot = pagination.snapshot;
        const revision = pagination.revision;
        const audience = identity();
        const ensureCurrent = () => {
            if (!isPublic() || identity() !== audience || initialConfig.signal?.aborted) throw new CanceledError('Public map audience changed');
        };
        let merged = response.data;
        const lastPage = Number(pagination.last_page);
        const total = Number(pagination.total);
        while (pagination.next_page != null) {
            ensureCurrent();
            const nextPage = Number(pagination.next_page);
            if (!snapshot || revision == null || !Number.isSafeInteger(nextPage)
                || nextPage !== Number(pagination.page) + 1 || nextPage > lastPage) throw new Error('Invalid public map pagination');
            const next = await http.request(nextRequestConfig(initialConfig, pagination));
            ensureCurrent();
            const nextPagination = next.data?.meta?.public_pagination;
            if (!nextPagination || nextPagination.snapshot !== snapshot || nextPagination.revision !== revision
                || Number(nextPagination.page) !== nextPage || Number(nextPagination.last_page) !== lastPage
                || Number(nextPagination.total) !== total) throw new Error('Public map snapshot changed');
            merged = mergePublicMapPages(merged, next.data);
            pagination = nextPagination;
        }
        return { ...response, data: merged };
    });
}
